import { graphql } from "@/config/graphql";
import { safeNumber } from "@/utils/number";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  InputAdornment,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { SupplySchema, supplySchema } from "../SupplyReportForm.schema";
import { UploadButton } from "@/components/UploadButton";

const Content = styled(Box)`
  padding: 16px 24px;
`;
const Form = styled("form")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const addSupplyDetailMutation = graphql(`
  mutation addSupplyDetail($params: AddSupplyDetailParams!) {
    addSupplyDetail(params: $params) {
      nextModified
      report {
        id
        supplyDetails {
          id
          resourceName
          initialQuantity
          finalQuantity
          incomeQuantity
          consumedQuantity
          comment
          photoUrl
        }
      }
    }
  }
`);
const removeSupplyDetailMutation = graphql(`
  mutation removeSupplyDetail($params: RemoveSupplyDetailParams!) {
    removeSupplyDetail(params: $params) {
      nextModified
      report {
        id
        supplyDetails {
          id
          resourceName
          initialQuantity
          finalQuantity
          incomeQuantity
          consumedQuantity
          comment
          photoUrl
        }
      }
    }
  }
`);
const updateSupplyDetailMutation = graphql(`
  mutation updateSupplyDetail($params: UpdateSupplyDetailParams!) {
    updateSupplyDetail(params: $params) {
      nextModified
      report {
        id
        supplyDetails {
          id
          resourceName
          initialQuantity
          finalQuantity
          incomeQuantity
          consumedQuantity
          comment
          photoUrl
        }
      }
    }
  }
`);

interface SuppliesReportFormProps {
  close: () => void;
  detail?: Partial<SupplySchema>;
}
export const SuppliesReportForm = (props: SuppliesReportFormProps) => {
  const params = useParams();
  const { control, handleSubmit, watch } = useForm<SupplySchema>({
    resolver: zodResolver(supplySchema),
    defaultValues: {
      resourceName: props.detail?.resourceName,
      initialQuantity: props.detail?.initialQuantity,
      finalQuantity: props.detail?.finalQuantity,
      incomeQuantity: props.detail?.incomeQuantity,
      comment: props.detail?.comment ?? "",
      photoUrl: props.detail?.photoUrl ?? undefined,
    },
  });

  const [addSupplyDetail, { loading: isAdding }] = useMutation(
    addSupplyDetailMutation
  );
  const [removeSupplyDetail, { loading: isRemoving }] = useMutation(
    removeSupplyDetailMutation,
    {
      variables: {
        params: { id: props.detail?.id ?? -1 },
      },
    }
  );
  const [updateSupplyDetail, { loading: isUpdating }] = useMutation(
    updateSupplyDetailMutation
  );

  const previousDetail = Boolean((props.detail?.id ?? 0) > 0);

  const handleClose = () => {
    props.close();
  };
  const handleRemove = async () => {
    await removeSupplyDetail();
    props.close();
  };
  const onSubmit = async (data: SupplySchema) => {
    if (previousDetail) {
      await updateSupplyDetail({
        variables: {
          params: {
            ...data,
            id: +(props.detail?.id ?? 0),
            resourceName: data.resourceName.replace(/^Otro: /g, ""),
          },
        },
      });
    } else {
      await addSupplyDetail({
        variables: {
          params: {
            ...data,
            reportId: +params.id!,
          },
        },
      });
    }
    handleClose();
  };

  const [resourceName, initialQuantity, finalQuantity, incomeQuantity] = watch([
    "resourceName",
    "initialQuantity",
    "finalQuantity",
    "incomeQuantity",
  ]);

  // if resource name ends with (UNIT) then unit is UNIT
  const unit = resourceName.match(/\(([^)]+)\)$/)?.[1] ?? "ton";
  const consumedQuantity =
    safeNumber(initialQuantity) -
    safeNumber(finalQuantity) +
    safeNumber(incomeQuantity);
  return (
    <>
      <Content>
        <Typography variant="h6">Agregar suministro</Typography>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Typography fontWeight={800}>
            Suministro:{" "}
            <Typography component="span" display={"inline"}>
              {resourceName}
            </Typography>
          </Typography>
          {(props.detail?.initialQuantity ?? null) !== null ? (
            <Typography fontWeight={800}>
              Saldo inicial:{" "}
              <Typography component="span" display={"inline"}>
                {Number(initialQuantity).toLocaleString("es-CL")}
              </Typography>
            </Typography>
          ) : (
            <Controller
              name="initialQuantity"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  label="Saldo inicial (+)"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  onWheel={() =>
                    (document.activeElement as HTMLInputElement)?.blur()
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{unit}</InputAdornment>
                    ),
                    inputProps: { inputMode: "decimal" },
                  }}
                  type="text"
                  {...field}
                ></TextField>
              )}
            />
          )}
          <Controller
            name="incomeQuantity"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="Ingresos (+)"
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                onWheel={() =>
                  (document.activeElement as HTMLInputElement)?.blur()
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">{unit}</InputAdornment>
                  ),
                  inputProps: { inputMode: "decimal" },
                }}
                type="text"
                {...field}
              ></TextField>
            )}
          />
          <Controller
            name="finalQuantity"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                label="Saldo final (-)"
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                onWheel={() =>
                  (document.activeElement as HTMLInputElement)?.blur()
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">{unit}</InputAdornment>
                  ),
                  inputProps: { inputMode: "decimal" },
                }}
                type="text"
                {...field}
              ></TextField>
            )}
          />

          <Controller
            name="comment"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Typography
                  fontWeight={800}
                  color={fieldState.error ? "error" : "inherit"}
                >
                  Consumo:{" "}
                  <Typography component="span" display={"inline"}>
                    {consumedQuantity.toLocaleString("es-CL")} {unit}
                  </Typography>
                </Typography>
                <TextField
                  label="Comentario"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  {...field}
                  value={field.value ?? ""}
                ></TextField>
              </>
            )}
          />
          {resourceName.includes("Diesel") && (
            <Controller
              name="photoUrl"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <UploadButton
                    value={field.value}
                    onChange={(e) => {
                      field.onChange({ target: { value: e } });
                    }}
                    error={!!fieldState.error}
                  >
                    Subir respaldo
                  </UploadButton>
                  {fieldState.error && (
                    <Typography color="error" variant="body2">
                      {fieldState.error.message}
                    </Typography>
                  )}
                </>
              )}
            />
          )}
        </Form>
      </Content>
      <DialogActions sx={{ padding: "8px 24px" }}>
        {previousDetail ? (
          <>
            <Button onClick={handleRemove} color="error" disabled={isRemoving}>
              ELIMINAR
              {isRemoving && (
                <CircularProgress sx={{ marginLeft: 1 }} size={20} />
              )}
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              color="info"
              disabled={isUpdating}
            >
              GUARDAR{" "}
              {isUpdating && (
                <CircularProgress sx={{ marginLeft: 1 }} size={20} />
              )}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} color="error">
              CANCELAR
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              color="info"
              disabled={isAdding}
            >
              GUARDAR{" "}
              {isAdding && (
                <CircularProgress sx={{ marginLeft: 1 }} size={20} />
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </>
  );
};
